// ─────────────────────────────────────────────────────────────────────────────
// Pure decision helpers for the btgpt ↔ WhatsApp "AI Conversation" bridge.
//
// A linked "AI Conversation" group is a solo WA group whose messages drive a
// headless btgpt turn server-side. Everything the user types (or records) there
// is `fromMe` — exactly like the AI's own replies, which are sent back into the
// group AS the user. The ONLY thing that separates user input from an AI echo is
// the hidden zero-width marker btgpt appends to every reply.
//
// These functions are intentionally side-effect free (no store, no fetch, no WA
// page globals) so the ingest decision can be unit-tested in isolation. The
// stateful membership lookup lives in ai-linked-chats-registry.ts; the wiring
// that consults both lives in contentMessageServiceHandlers.ts.
// ─────────────────────────────────────────────────────────────────────────────

import { hasAnyHiddenMarker } from '@bt-common/wa-events/ai-conversation-marker';

/** Voice-note message types WhatsApp emits — push-to-talk and attached audio. */
export function isVoiceNoteMessageType(type?: string): boolean {
  return type === 'ptt' || type === 'audio';
}

/**
 * Resolve the chat id (`@g.us`) a lean own-message payload belongs to. Kept
 * pure/defensive because the payload shape varies (nested wid-like `id.remote`
 * vs flat serialized strings) across WA builds and the @lid→@c.us normalizer.
 */
export function resolveAiLinkedChatId(msg: any): string | undefined {
  const remote = msg?.id?.remote;
  const candidate =
    (typeof remote === 'string' ? remote : remote?._serialized) ||
    (typeof msg?.to === 'string' ? msg?.to : msg?.to?._serialized) ||
    (typeof msg?.chatId === 'string' ? msg?.chatId : msg?.chatId?._serialized);
  return typeof candidate === 'string' && candidate.length > 0 ? candidate : undefined;
}

/**
 * The linked-chat ingest gate. A message opens the aiConversation lane only
 * when it is the user's own message (`fromMe`) in a linked chat and does NOT
 * carry a hidden marker (i.e. it isn't btgpt's own reply echoing back).
 *
 * Marked/echo messages and messages in non-linked chats return false and are
 * handled exactly as before this feature existed.
 */
export function shouldIngestAiLinkedChatMessage(params: {
  fromMe: boolean;
  isLinkedChat: boolean;
  body?: string;
}): boolean {
  return (
    params.fromMe === true &&
    params.isLinkedChat === true &&
    !hasAnyHiddenMarker(params.body)
  );
}

/**
 * Mark a lean message so the relevance predicate opens the aiConversation lane
 * (client-side send decision) and the backend receives the bridge turn input.
 *
 * `transcribedText` is attached as a TOP-LEVEL field on the lean message — the
 * backend keys on `msg.transcribedText` for voice notes (falls back to `body`).
 */
export function markAiLinkedChatEnvelope<T extends Record<string, any>>(
  msg: T,
  opts?: { transcribedText?: string; transcriptionFailed?: boolean },
): T & { aiLinkedChat: true; transcribedText?: string; transcriptionFailed?: boolean } {
  const enriched: any = { ...msg, aiLinkedChat: true };
  const transcribedText = opts?.transcribedText;
  if (typeof transcribedText === 'string' && transcribedText.length > 0) {
    enriched.transcribedText = transcribedText;
  }
  // Voice note whose transcription failed/timed out — the backend sends an
  // in-chat notice instead of running a turn (never a silent drop).
  if (opts?.transcriptionFailed === true) {
    enriched.transcriptionFailed = true;
  }
  return enriched;
}
